export type LogEntry = {
  id_log: number
  name: string
  type: string
  id_log_type?: number
  type_id?: number
  user_id: number | null
  user_guid?: string | null
  user: string | null
  created_at: string
  data: unknown
}

type LogDate = string | Date | number | null | undefined

/** Stored payloads may already be decoded, JSON strings, or legacy plain text. */
export const parseLogData = (data: unknown): unknown => {
  if (typeof data !== 'string') return data
  try {
    return JSON.parse(data)
  } catch {
    return data
  }
}

/** SQL timestamps have no timezone; interpret them in the same local time as the filters. */
export const logTimestamp = (value: LogDate): number => {
  if (value == null || value === '') return NaN
  if (value instanceof Date) return value.getTime()
  if (typeof value === 'number') return new Date(value).getTime()

  const normalized = value.trim().replace(' ', 'T')
  const match = normalized.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?(Z|[+-]\d{2}:?\d{2})?)?$/
  )
  if (!match) return NaN

  const [, yearText, monthText, dayText, hourText, minuteText, secondText, fraction, zone] = match
  const year = Number(yearText)
  const month = Number(monthText)
  const day = Number(dayText)
  const hour = Number(hourText || 0)
  const minute = Number(minuteText || 0)
  const second = Number(secondText || 0)
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
  const daysInMonth = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1]
  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > daysInMonth ||
    hour > 23 ||
    minute > 59 ||
    second > 59
  ) {
    return NaN
  }

  if (zone) return new Date(normalized).getTime()
  const local = new Date(0)
  local.setFullYear(year, month - 1, day)
  local.setHours(hour, minute, second, Number((fraction || '').padEnd(3, '0').slice(0, 3)))
  return local.getTime()
}

export const formatLogDate = (value: LogDate, options?: Intl.DateTimeFormatOptions): string => {
  const timestamp = logTimestamp(value)
  if (!Number.isFinite(timestamp)) return 'Data indisponível'
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
    ...options,
  }).format(timestamp)
}

export const dateToInput = (value: LogDate): string => {
  const timestamp = logTimestamp(value)
  if (!Number.isFinite(timestamp)) return ''
  const date = new Date(timestamp)
  const pad = (part: number) => String(part).padStart(2, '0')
  return `${String(date.getFullYear()).padStart(4, '0')}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

export const initials = (name: string | null | undefined): string => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return 'SI'
  return `${parts[0][0]}${parts.length > 1 ? parts[parts.length - 1][0] : ''}`.toLocaleUpperCase(
    'pt-BR'
  )
}
